import { get, send } from "../api.js";

export interface ScheduleBlockInput {
  block_date: string;
  end_date?: string | null;
  start_time?: string | null;
  end_time?: string | null;
  reason: string;
}

export interface ScheduleBlockConflict {
  appointment_id: number;
  starts_at: string;
  ends_at: string;
  customer_names: string[];
}

export interface ScheduleBlockPreview {
  provider_id: number;
  block_date: string;
  end_date: string;
  start_time: string | null;
  end_time: string | null;
  reason: string;
  all_day: boolean;
  block_created: boolean;
  conflict_count: number;
  warning: string | null;
  conflicting_appointments: ScheduleBlockConflict[];
}

export interface StaffScheduleBlock {
  id: string;
  provider_id: number;
  block_date: string;
  end_date: string;
  start_time: string | null;
  end_time: string | null;
  reason: string;
  status: string;
  created_by_staff_id: string | null;
  removed_by_staff_id: string | null;
  last_error: string | null;
  removed_at: string | null;
  created_at: string;
  updated_at: string;
}

export type StaffScheduleBlockStatus = "active" | "requires_attention";

export interface StaffScheduleBlockPage {
  items: StaffScheduleBlock[];
  total: number;
  page: number;
  limit: number;
  total_pages: number;
}

/** What checking Amelia for one date found. */
export interface ScheduleBlockRemovalConfirmation {
  provider_id: number;
  block_date: string;
  /** True when Amelia has nothing for the date and the blocks are now removed. */
  confirmed: boolean;
  message: string;
  removed_block_ids: string[];
}

export interface ScheduleBlockApply {
  block: StaffScheduleBlock;
  schedule: ScheduleBlockPreview;
}

const root = "/staff-api/api/v1/schedule";

function isWholeNumber(value: unknown, min: number): value is number {
  return typeof value === "number" && Number.isInteger(value) && value >= min;
}

function isBlock(value: unknown): value is StaffScheduleBlock {
  if (typeof value !== "object" || value === null) return false;
  const row = value as Record<string, unknown>;
  return typeof row.id === "string" && typeof row.status === "string" && typeof row.block_date === "string";
}

/**
 * A backend without the paged contract answers the same request with the whole
 * list as a bare array. That array is the complete list for the requested
 * status, so it is paged here. Anything else that is not a well-formed page is
 * refused: an unknown shape must never read as "no blocks".
 */
export function normaliseBlockPage(body: unknown, page: number, limit: number): StaffScheduleBlockPage {
  if (Array.isArray(body)) {
    if (!body.every(isBlock)) throw new Error("The schedule-block list was not in the expected shape.");
    const totalPages = Math.max(1, Math.ceil(body.length / limit));
    const current = Math.min(Math.max(1, page), totalPages);
    return {
      items: body.slice((current - 1) * limit, current * limit),
      total: body.length,
      page: current,
      limit,
      total_pages: totalPages,
    };
  }
  if (typeof body === "object" && body !== null) {
    const row = body as Record<string, unknown>;
    if (
      Array.isArray(row.items) &&
      row.items.every(isBlock) &&
      isWholeNumber(row.total, 0) &&
      isWholeNumber(row.page, 1) &&
      isWholeNumber(row.limit, 1) &&
      isWholeNumber(row.total_pages, 1) &&
      row.items.length <= row.limit
    ) {
      return row as unknown as StaffScheduleBlockPage;
    }
  }
  throw new Error("The schedule-block list was not in the expected shape.");
}

export const scheduleBlocks = {
  list: (providerId: number, startDate?: string, endDate?: string) =>
    get<StaffScheduleBlock[]>(`${root}/blocks`, {
      provider_id: String(providerId),
      block_status: "active",
      start_date: startDate,
      end_date: endDate,
    }),
  /**
   * Blocks whose effect in Amelia could not be confirmed: a create or edit
   * whose saved entry was not read back, or a removal Amelia kept. They are
   * not active, so `list` leaves them out, but they may still be blocking
   * time — the tutor has to see them to sort them out.
   */
  needingReview: (providerId: number) =>
    get<StaffScheduleBlock[]>(`${root}/blocks`, {
      provider_id: String(providerId),
      block_status: "requires_attention",
    }),
  listPage: async (
    providerId: number,
    blockStatus: StaffScheduleBlockStatus,
    page: number,
    limit: number,
  ): Promise<StaffScheduleBlockPage> =>
    normaliseBlockPage(
      await get<unknown>(`${root}/blocks`, {
        provider_id: String(providerId),
        block_status: blockStatus,
        page: String(page),
        limit: String(limit),
      }),
      page,
      limit,
    ),
  read: (providerId: number, blockId: string) =>
    get<StaffScheduleBlock>(
      `${root}/providers/${providerId}/blocks/${encodeURIComponent(blockId)}`,
    ),
  /**
   * Ask the backend to look at Amelia for one date. When Amelia has nothing
   * there, every block for that date is marked removed. Only a block that
   * starts and ends on that date can be settled this way.
   */
  confirmRemoval: (providerId: number, blockDate: string) =>
    send<ScheduleBlockRemovalConfirmation>(
      "POST",
      `${root}/providers/${providerId}/blocks/confirm-removal`,
      { block_date: blockDate },
    ),
  preview: (providerId: number, input: ScheduleBlockInput) =>
    send<ScheduleBlockPreview>("POST", `${root}/providers/${providerId}/blocks/preview`, input),
  create: (providerId: number, input: ScheduleBlockInput) =>
    send<ScheduleBlockApply>("POST", `${root}/providers/${providerId}/blocks`, input),
  update: (providerId: number, blockId: string, input: ScheduleBlockInput) =>
    send<ScheduleBlockApply>(
      "PATCH",
      `${root}/providers/${providerId}/blocks/${encodeURIComponent(blockId)}`,
      input,
    ),
  remove: (providerId: number, blockId: string) =>
    send<StaffScheduleBlock>(
      "DELETE",
      `${root}/providers/${providerId}/blocks/${encodeURIComponent(blockId)}`,
    ),
};
